import { ActionButton } from "@via/components/action-button/action-button.tsx";
import Modal from "@via/components/modal.tsx";
import { useUserConfigurations } from "@via/api/configuration";
import {
  type AppLaunchEnvironment,
  refusalText,
  useAppLaunchEnvironments,
  useCreateHpcAppLaunch,
} from "@via/pages/apps/hpc/hpc-apps.api.tsx";
import { ModalSize, ROUTES } from "@via/utils/constants.tsx";
import {
  type AppKind,
  type AppListResponseBodyItem,
  type MountMode,
  PHASE1_APP_KINDS,
} from "common";
import { type JSX, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export const RUN_ON_CLUSTER = "Run on my cluster";
export const NEEDS_AGENT_ENV_LINE =
  "Needs an agent run environment with apps enabled";
export const EGRESS_LINE =
  "The app can reach any address the compute node can reach. Foundry Connect does not restrict its outbound traffic.";
export const MEMORY_NOT_ENFORCED_LINE =
  "This cluster does not cap memory, so the app is not stopped when it uses more than it asked for.";
export const SAME_NODE_LINE =
  "Other accounts on the same compute node can reach a Shiny app directly, because this environment does not reserve a whole node for each app.";

/** What a launch gets when neither the request nor the app says. Mirrors the backend's kind defaults. */
export const APP_KIND_RESOURCE_DEFAULTS = {
  cpus: 1,
  memoryMb: 4096,
  wallMinutes: 240,
} as const;

const CLUSTER_LAUNCHES_PATH = "/apps/cluster-launches";

const isPhase1Kind = (kind: AppKind | null | undefined): boolean =>
  Boolean(kind) && PHASE1_APP_KINDS.includes(kind as AppKind);

/**
 * Whether the action applies to this app at all: HPC apps on, a Phase 1 kind (shiny, jupyter or marimo) and
 * an image. The environment check comes after.
 */
export const runOnClusterApplies = (
  app: Pick<AppListResponseBodyItem, "appKind" | "image">,
  hpcAppsEnabled: boolean
): boolean => hpcAppsEnabled && isPhase1Kind(app.appKind) && Boolean(app.image);

/** The "Run on my cluster" action of a My Apps row, and its dialog. Nothing while it does not apply. */
export function RunOnClusterAction({
  app,
}: {
  app: AppListResponseBodyItem;
}): JSX.Element | null {
  const navigate = useNavigate();
  const { data: configurations } = useUserConfigurations();
  const applies = runOnClusterApplies(
    app,
    Boolean(configurations?.HPC_APPS_ENABLED)
  );
  const { environments, isLoading } = useAppLaunchEnvironments(applies);
  const [open, setOpen] = useState(false);

  if (!applies) return null;
  const noEnvironment = !isLoading && environments.length === 0;

  return (
    <>
      <ActionButton
        id={`run-on-cluster-${app.id}`}
        button={RUN_ON_CLUSTER}
        primary={false}
        showIcon={false}
        disabled={isLoading || noEnvironment}
        title={noEnvironment ? NEEDS_AGENT_ENV_LINE : undefined}
        className="shrink-0 whitespace-nowrap"
        onClick={() => setOpen(true)}
      />
      {open ? (
        <Modal
          open={open}
          setOpen={setOpen}
          id={`run-on-cluster-modal-${app.id}`}
          title={`Run ${app.name} on my cluster`}
          size={ModalSize.Large}
          showButtons={false}>
          <LaunchForm
            app={app}
            environments={environments}
            onLaunched={() => {
              setOpen(false);
              navigate(CLUSTER_LAUNCHES_PATH);
            }}
          />
        </Modal>
      ) : null}
    </>
  );
}

/**
 * The page's one "needs an agent run environment" notice, shown above the apps table when HPC apps
 * are on, some listed app could run on a cluster, and no environment passed the app check. Each
 * row's disabled button keeps the same sentence as its tooltip.
 */
export function AgentEnvironmentNotice({
  apps,
}: {
  apps: ReadonlyArray<Pick<AppListResponseBodyItem, "appKind" | "image">>;
}): JSX.Element | null {
  const { data: configurations } = useUserConfigurations();
  const hpcAppsEnabled = Boolean(configurations?.HPC_APPS_ENABLED);
  const applies = apps.some(app => runOnClusterApplies(app, hpcAppsEnabled));
  const { environments, isLoading } = useAppLaunchEnvironments(applies);

  if (!applies || isLoading || environments.length > 0) return null;
  return (
    <p className="mb-2 text-sm text-gray-600">
      <span>{NEEDS_AGENT_ENV_LINE}</span>{" "}
      <Link to={ROUTES.RUN_ENVIRONMENTS} className="via-link">
        Run environments
      </Link>
    </p>
  );
}

interface ResourceLimits {
  floor: number | null;
  cap: number;
}

/** The value a field starts at: the app's own, else the kind default, never above the cap. */
const prefill = (
  own: number | null | undefined,
  fallback: number,
  cap: number
): number => Math.min(own ?? fallback, cap);

/** A typed value is held to the cap. Below the floor it is kept as typed and refused. */
const clampToCap = (raw: string, cap: number): string => {
  const value = Number(raw);
  return raw.trim() !== "" && Number.isFinite(value) && value > cap
    ? String(cap)
    : raw;
};

const parseWhole = (raw: string): number | null => {
  const value = Number(raw);
  return raw.trim() !== "" && Number.isInteger(value) && value >= 1
    ? value
    : null;
};

/** Why a cpus or memory value cannot launch, or null when it can. */
const resourceProblem = (
  raw: string,
  limits: ResourceLimits,
  words: {
    floorAboveCap: (floor: number, cap: number) => string;
    belowFloor: (floor: number) => string;
  }
): string | null => {
  if (limits.floor !== null && limits.floor > limits.cap) {
    return words.floorAboveCap(limits.floor, limits.cap);
  }
  const value = parseWhole(raw);
  if (value === null) return "Enter a whole number of at least 1.";
  if (limits.floor !== null && value < limits.floor) {
    return words.belowFloor(limits.floor);
  }
  return null;
};

interface MountRow {
  hostPath: string;
  containerPath: string;
  mode: MountMode;
}

const inputClass =
  "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm";
const secondaryButton =
  "rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";
const primaryButton =
  "rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50";

const resourcesFor = (
  app: AppListResponseBodyItem,
  target: AppLaunchEnvironment | undefined
): { cpus: string; memoryMb: string; wallMinutes: string } => {
  const caps = target?.settings.appCaps;
  if (!caps) return { cpus: "", memoryMb: "", wallMinutes: "" };
  return {
    cpus: String(
      prefill(app.appCpus, APP_KIND_RESOURCE_DEFAULTS.cpus, caps.maxCpus)
    ),
    memoryMb: String(
      prefill(
        app.appMemoryMb,
        APP_KIND_RESOURCE_DEFAULTS.memoryMb,
        caps.maxMemoryMb
      )
    ),
    wallMinutes: String(
      prefill(
        app.appWallMinutes,
        APP_KIND_RESOURCE_DEFAULTS.wallMinutes,
        caps.maxWallMinutes
      )
    ),
  };
};

/**
 * The launch dialog's body. The app's cpus and memory are a floor (agent-d53): a value below it is
 * refused here with a line, never raised, and an app whose floor is above the environment's cap
 * cannot launch there at all. Walltime is only a default, held to the environment's cap.
 */
export function LaunchForm({
  app,
  environments,
  onLaunched,
}: {
  app: AppListResponseBodyItem;
  environments: AppLaunchEnvironment[];
  onLaunched: () => void;
}): JSX.Element {
  const create = useCreateHpcAppLaunch();
  const [environmentId, setEnvironmentId] = useState<number | undefined>(
    environments[0]?.environment.id
  );
  const target = environments.find(e => e.environment.id === environmentId);
  const [resources, setResources] = useState(() => resourcesFor(app, target));
  const [mounts, setMounts] = useState<MountRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const caps = target?.settings.appCaps ?? null;
  const apps = target?.settings.apps ?? null;

  const chooseEnvironment = (id: number): void => {
    setEnvironmentId(id);
    setResources(
      resourcesFor(
        app,
        environments.find(e => e.environment.id === id)
      )
    );
    setError(null);
  };

  const cpusProblem = caps
    ? resourceProblem(
        resources.cpus,
        { floor: app.appCpus ?? null, cap: caps.maxCpus },
        {
          floorAboveCap: (floor, cap) =>
            `This app needs ${floor} cpus, above this environment's limit of ${cap}.`,
          belowFloor: floor => `This app needs at least ${floor} cpus.`,
        }
      )
    : null;
  const memoryProblem = caps
    ? resourceProblem(
        resources.memoryMb,
        { floor: app.appMemoryMb ?? null, cap: caps.maxMemoryMb },
        {
          floorAboveCap: (floor, cap) =>
            `This app needs ${floor} MB of memory, above this environment's limit of ${cap} MB.`,
          belowFloor: floor => `This app needs at least ${floor} MB of memory.`,
        }
      )
    : null;
  const wallProblem =
    caps && parseWhole(resources.wallMinutes) === null
      ? "Enter a whole number of at least 1."
      : null;

  const blocked =
    !target || Boolean(cpusProblem || memoryProblem || wallProblem);

  const launch = async (): Promise<void> => {
    if (!target || blocked) return;
    setError(null);
    try {
      await create.mutateAsync({
        appId: app.id,
        profileClusterId: target.environment.id,
        cpus: parseWhole(resources.cpus)!,
        memoryMb: parseWhole(resources.memoryMb)!,
        wallMinutes: parseWhole(resources.wallMinutes)!,
        mounts: mounts
          .filter(mount => mount.hostPath.trim() !== "")
          .map(mount => ({
            hostPath: mount.hostPath.trim(),
            containerPath: mount.containerPath.trim(),
            mode: mount.mode,
          })),
      });
      onLaunched();
    } catch (failure) {
      setError(refusalText(failure, "The app could not be launched."));
    }
  };

  const editMount = (index: number, patch: Partial<MountRow>): void =>
    setMounts(previous =>
      previous.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );

  const numberField = (
    id: string,
    label: string,
    key: keyof typeof resources,
    cap: number | undefined,
    problem: string | null
  ): JSX.Element => (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium leading-6 text-gray-900">
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={1}
        max={cap}
        className={`mt-1 ${inputClass}`}
        value={resources[key]}
        onChange={event => {
          const raw = event.target.value;
          setResources(previous => ({
            ...previous,
            [key]: cap === undefined ? raw : clampToCap(raw, cap),
          }));
        }}
      />
      {cap !== undefined ? (
        <p className="mt-1 text-xs text-gray-500">At most {cap}</p>
      ) : null}
      {problem ? <p className="mt-1 text-sm text-red-600">{problem}</p> : null}
    </div>
  );

  return (
    <div className="space-y-5 py-2">
      <div>
        <label
          htmlFor="hpc-launch-environment"
          className="block text-sm font-medium leading-6 text-gray-900">
          Run environment
        </label>
        <select
          id="hpc-launch-environment"
          className={`mt-1 ${inputClass}`}
          value={environmentId ?? ""}
          onChange={event => chooseEnvironment(Number(event.target.value))}>
          {environments.map(({ environment }) => (
            <option key={environment.id} value={environment.id}>
              {environment.name}
            </option>
          ))}
        </select>
        {environments.length === 0 ? (
          <p className="mt-1 text-sm text-gray-600">{NEEDS_AGENT_ENV_LINE}</p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {numberField(
          "hpc-launch-cpus",
          "Cpus",
          "cpus",
          caps?.maxCpus,
          cpusProblem
        )}
        {numberField(
          "hpc-launch-memory",
          "Memory (MB)",
          "memoryMb",
          caps?.maxMemoryMb,
          memoryProblem
        )}
        {numberField(
          "hpc-launch-wall",
          "Walltime (minutes)",
          "wallMinutes",
          caps?.maxWallMinutes,
          wallProblem
        )}
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-900">Data mounts</p>
        <p className="text-xs text-gray-500">
          Optional. A folder inside one of this environment&apos;s data folders,
          seen by the app under /mnt. Read only unless you choose otherwise.
        </p>
        {mounts.map((mount, index) => (
          <div
            key={index}
            className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto_auto]">
            <input
              aria-label={`Host folder ${index + 1}`}
              className={inputClass}
              placeholder="/data/project"
              value={mount.hostPath}
              onChange={event =>
                editMount(index, { hostPath: event.target.value })
              }
            />
            <input
              aria-label={`Seen by the app at ${index + 1}`}
              className={inputClass}
              placeholder="/mnt/project"
              value={mount.containerPath}
              onChange={event =>
                editMount(index, { containerPath: event.target.value })
              }
            />
            <select
              aria-label={`Access ${index + 1}`}
              className={inputClass}
              value={mount.mode}
              onChange={event =>
                editMount(index, { mode: event.target.value as MountMode })
              }>
              <option value="ro">Read only</option>
              <option value="rw">Read and write</option>
            </select>
            <button
              type="button"
              className={secondaryButton}
              onClick={() =>
                setMounts(previous => previous.filter((_, i) => i !== index))
              }>
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className={secondaryButton}
          onClick={() =>
            setMounts(previous => [
              ...previous,
              { hostPath: "", containerPath: "/mnt/", mode: "ro" },
            ])
          }>
          Add a data mount
        </button>
      </div>

      <div className="space-y-2 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
        <p data-testid="hpc-launch-egress">{EGRESS_LINE}</p>
        {apps?.memoryEnforcement === "not_enforced" ? (
          <p data-testid="hpc-launch-memory-not-enforced">
            {MEMORY_NOT_ENFORCED_LINE}
          </p>
        ) : null}
        {app.appKind === "shiny" && apps && !apps.requireIsolatedNode ? (
          <p data-testid="hpc-launch-same-node">{SAME_NODE_LINE}</p>
        ) : null}
        <p>
          The app waits in the cluster&apos;s queue like any job, and you can
          stop it at any time.
        </p>
      </div>

      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex justify-end">
        <button
          type="button"
          className={primaryButton}
          disabled={blocked || create.isPending}
          onClick={launch}>
          Launch
        </button>
      </div>
    </div>
  );
}
